export const LEGAL_LOCALES = ["de", "en"] as const;
export type LegalLocale = (typeof LEGAL_LOCALES)[number];

/** Church legal pages editable in Command Center and shown in the app Me tab. */
export const LEGAL_DOCUMENT_KINDS = ["privacy", "imprint", "terms"] as const;
export type LegalDocumentKind = (typeof LEGAL_DOCUMENT_KINDS)[number];

export type LegalDocumentsStore = Partial<Record<LegalDocumentKind, Partial<Record<LegalLocale, string>>>>;

/** Global app legal copy in `settings` (church_id NULL). */
export const APP_LEGAL_SETTINGS_KEY = "legal_documents";

const emptyLocaleMap = (): Partial<Record<LegalLocale, string>> => ({});

/** Trim; treat nbsp-only and whitespace as empty (rich editor often leaves these). */
export function cleanLegalText(v: string | null | undefined): string | null {
  if (v == null) return null;
  const t = v.replace(/\u00a0/g, " ").trim();
  return t.length ? t : null;
}

/** Parse legal store from a settings row value or nested `legalDocuments` on church jsonb. */
export function legalDocumentsFromValue(value: unknown): LegalDocumentsStore {
  if (!value || typeof value !== "object") return {};
  return parseLegalDocuments({ legalDocuments: value });
}

/** Parse `churches.settings.legalDocuments` from jsonb. */
export function parseLegalDocuments(settings: unknown): LegalDocumentsStore {
  if (!settings || typeof settings !== "object") return {};
  const raw = (settings as { legalDocuments?: unknown }).legalDocuments;
  if (!raw || typeof raw !== "object") return {};
  const out: LegalDocumentsStore = {};
  for (const kind of LEGAL_DOCUMENT_KINDS) {
    const block = (raw as Record<string, unknown>)[kind];
    if (!block || typeof block !== "object") continue;
    const locales = emptyLocaleMap();
    for (const loc of LEGAL_LOCALES) {
      const v = (block as Record<string, unknown>)[loc];
      const cleaned = typeof v === "string" ? cleanLegalText(v) : null;
      if (cleaned) locales[loc] = cleaned;
    }
    if (Object.keys(locales).length) out[kind] = locales;
  }
  return out;
}

/**
 * Pick markdown for the viewer locale — same fallback as `@heylife/i18n` `pickText`:
 * requested locale → de → en → any stored value.
 */
export function legalDocumentMarkdown(store: LegalDocumentsStore, kind: LegalDocumentKind, locale: LegalLocale): string | null {
  const block = store[kind];
  if (!block) return null;
  const raw = block[locale] ?? block.de ?? block.en ?? Object.values(block)[0];
  return typeof raw === "string" ? cleanLegalText(raw) : null;
}

/** Kinds that have at least one non-empty locale string. */
export function legalDocumentKindsAvailable(store: LegalDocumentsStore): LegalDocumentKind[] {
  return LEGAL_DOCUMENT_KINDS.filter((k) => LEGAL_LOCALES.some((loc) => Boolean(cleanLegalText(store[k]?.[loc]))));
}

/** Merge a single kind into existing settings (for PATCH). */
export function mergeLegalDocument(
  settings: Record<string, unknown>,
  kind: LegalDocumentKind,
  locales: Partial<Record<LegalLocale, string | null | undefined>>,
): Record<string, unknown> {
  const current = parseLegalDocuments(settings);
  const nextBlock: Partial<Record<LegalLocale, string>> = { ...current[kind] };
  for (const loc of LEGAL_LOCALES) {
    if (!(loc in locales)) continue;
    const v = locales[loc];
    const cleaned = cleanLegalText(v == null ? null : String(v));
    if (!cleaned) delete nextBlock[loc];
    else nextBlock[loc] = cleaned;
  }
  const nextStore: LegalDocumentsStore = { ...current };
  if (Object.keys(nextBlock).length === 0) delete nextStore[kind];
  else nextStore[kind] = nextBlock;
  return { ...settings, legalDocuments: nextStore };
}

/** Merge one kind into a flat legal store (platform settings value). */
export function mergeLegalDocumentStore(
  store: LegalDocumentsStore,
  kind: LegalDocumentKind,
  locales: Partial<Record<LegalLocale, string | null | undefined>>,
): LegalDocumentsStore {
  const settings = mergeLegalDocument({ legalDocuments: store }, kind, locales);
  return parseLegalDocuments(settings);
}
